import { type KeyboardEventHandler } from "react";

import { Link as RouterLink } from "react-router-dom";
import { Box, Card, Radio, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ssoTexts } from "../../../../assets/texts/Settings/sso";
import useRouteMatchURL from "../../../../Components/hooks/useRouteMatchURL";
import { type AuthState, type SSOProviders } from "../types";

type Props = {
  type: SSOProviders;
  description: string;
  selected: boolean;
  configured: boolean;
  ssoEnabled: boolean;
  openProviderDialog: () => void;
  state: AuthState;
};

export const SsoProviderConfigCard = ({
  configured,
  openProviderDialog,
  type,
  description,
  selected,
  ssoEnabled,
  state,
}: Props) => {
  const routeMatchURL = useRouteMatchURL();
  const theme = useTheme();

  const handleKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      openProviderDialog();
    }
  };

  return (
    <Card sx={{ flex: 1, borderColor: selected && ssoEnabled ? "primary.main" : "", borderWidth: selected ? 2 : 1 }}>
      <Stack direction="row" sx={{ mb: 2 }}>
        <Box sx={{ mt: 1, ml: 1 }}>
          <Radio
            size="small"
            color={configured ? "primary" : "default"}
            onChange={openProviderDialog}
            checked={selected}
            disabled={!configured || !ssoEnabled}
            sx={{ color: ssoEnabled && configured ? "text.primary" : "action.disabled" }}
            onKeyDown={handleKeyDown}
            data-cy={`${type}-radio`}
          />
        </Box>
        <Stack spacing={1.5} sx={{ margin: theme.spacing(2, 2, 0, 1), width: "100%" }}>
          <Typography variant="body1" sx={{ color: configured && ssoEnabled ? "text.primary" : "action.disabled" }}>
            <strong>{type.toUpperCase()}</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: configured && ssoEnabled ? "text.secondary" : "action.disabled" }}>
            {description}
          </Typography>
        </Stack>
        <Box sx={{ margin: theme.spacing(4, 2, 0, 2) }}>
          <Typography
            sx={{ whiteSpace: "nowrap", textDecoration: "none" }}
            component={RouterLink}
            to={state.loading || (!ssoEnabled && configured) ? "#" : `${routeMatchURL}/sso/${type}`}
            color={state.loading || (!ssoEnabled && configured) ? "action.disabled" : "primary"}
            variant="body2"
            data-cy={`${type}-configure`}
          >
            {configured ? ssoTexts.EDIT_CONFIGURATION : ssoTexts.CONFIGURE}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
};
